<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcRentalFormServicesTable',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    editable: {
      type: Boolean,
      required: false,
      default: true,
    },
    rentalId: {
      type: String,
      required: true,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const tableServices = ref(null);

  const $refs = {
    get tableServices() {
      return tableServices.value;
    },
  };

  const onMeterSelected = (...args) => {
    const { col, option, row, value } = args[0].data;

    if (value && value.unit) {
      row.data.billing_unit = value.unit;
    }
  };

  const items = ref(null);
  const units = ref(null);
  const ux = ref({
    initialLoadSpinner: true,
  });
  const b_7315092382398562_columns = computed(() => [
    {
      name: 'locked',
      type: 'lock',
    },
    {
      name: 'inactive',
      type: 'checkbox',
      header: $fn.value.gettext('Inactive'),
      width: '90px',
      textAlign: 'center',
    },
    {
      name: 'valid_from',
      type: 'date',
      header: $fn.value.gettext('Valid From'),
      width: '120px',
    },
    {
      name: 'valid_to',
      type: 'date',
      header: $fn.value.gettext('Valid To'),
      width: '120px',
    },
    {
      name: 'service',
      type: 'select2',
      header: $fn.value.gettext('Service'),
      width: '250px',
      required: true,
      path: 'name',
      bindObject: true,
      textField: 'name',
      options: $fn.value.searchDataSource('suggest_product', {
        product_types: ['service'],
      }),
    },
    {
      name: 'meter',
      type: 'select2',
      header: $fn.value.gettext('Meter'),
      width: '250px',
      path: 'serial_number',
      bindObject: true,
      textField: 'serial_number',
      options: $fn.value.searchDataSource('suggest_meter', {
        t: 'rentalService',
      }),
      formatter: {
        name: 'template',
        value:
          "<b-link v-if=\"row.meter_id || (row.meter && row.meter.id)\" :to=\"{\n\
  name: 'app.meter',\n\
  params: { id: row.meter_id || row.meter.id }\n\
}\">{{ row.meter ? row.meter.serial_number : '-' }}</b-link>\n\
<span v-else>{{ row.meter ? row.meter.serial_number : '-' }}</span>\n\
",
      },
    },
    {
      name: 'billing_unit',
      type: 'select2',
      header: $fn.value.gettext('Billing Unit'),
      width: '120px',
      path: 'billing_unit',
      valueField: 'symbol',
      textField: 'symbol',
      required: true,
      options: units.value,
    },
    {
      name: 'meter_factor',
      type: 'number',
      header: $fn.value.gettext('Factor'),
      width: '100px',
    },
    {
      name: 'notes',
      header: $fn.value.gettext('Notes'),
      width: '320px',
    },
  ]);

  const $data = computed(() => ({
    items: items.value,
    units: units.value,
    ux: ux.value,
    b_7315092382398562_columns: b_7315092382398562_columns.value,
  }));

  const $$load_items = () => {
    return $fn.value
      .fetch('list_rental_service', {
        parameters: { rental_id: props.rentalId },
      })
      .then((d) => _.orderBy(d, ['id']));
  };
  const $$load_units = () => {
    return $fn.value
      .fetch('list_unit')
      .then((d) => _.orderBy(d, [(p) => p.symbol.toLowerCase()]));
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'items' },
      { name: 'units' },
      { name: 'ux' },
      {
        name: 'b_7315092382398562_columns',
        nodeId: '7315092382398562',
        dependsOn: ['units'],
      },
    ],
    loaders: { items: $$load_items, units: $$load_units },
    ux: { initialLoadSpinner: true },
    dataRefs: { items, units, ux, b_7315092382398562_columns },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.editable,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('editable');
    }
  );
  watch(
    () => props.rentalId,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('rentalId');
    }
  );

  const act_7315092382398562_script_t01n6kqv5_value_callable = async (
    $event
  ) => {
    return $event.data.waitFor(
      $fn.value.crud('rental_service').upsert({
        ...$event.data.row,
        asset_id: $event.data.row.asset_id,
        service_id: $event.data.row.service.id,
        meter_id: _.get($event.data.row, 'meter.id'),
      })
    );
  };
  const act_7315092382398562_script_t01n6kqv5_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_7315092382398562_script_t01n6kqv5_value_callable($event);
      },
    };
  };
  const act_7315092382398562_script_gz1qtkamn_value_callable = async (
    $event
  ) => {
    return $event.data.waitFor(
      $fn.value.crud('rental_service').delete($event.data.row)
    );
  };
  const act_7315092382398562_script_gz1qtkamn_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_7315092382398562_script_gz1qtkamn_value_callable($event);
      },
    };
  };
  const act_7315092382398562_script_1v30phlnb_value_callable = async (
    $event
  ) => {
    return onMeterSelected($event);
  };
  const act_7315092382398562_script_1v30phlnb_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_7315092382398562_script_1v30phlnb_value_callable($event);
      },
    };
  };

  const act_7315092382398562_script_t01n6kqv5 = new actionTypes.ScriptAction({
    actionArgs: act_7315092382398562_script_t01n6kqv5_getActionArgs,
  });
  const inst_evh_7315092382398562_save_gf1z7uszn =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_script_t01n6kqv5,
      event: 'save',
      displayName: 'script',
    });
  const inst_evh_7315092382398562_save = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_save_gf1z7uszn],
    event: 'save',
  });
  const act_7315092382398562_script_gz1qtkamn = new actionTypes.ScriptAction({
    actionArgs: act_7315092382398562_script_gz1qtkamn_getActionArgs,
  });
  const inst_evh_7315092382398562_delete_m4rzawhra =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_script_gz1qtkamn,
      event: 'delete',
      displayName: 'script',
    });
  const inst_evh_7315092382398562_delete = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_delete_m4rzawhra],
    event: 'delete',
  });
  const act_7315092382398562_script_1v30phlnb = new actionTypes.ScriptAction({
    actionArgs: act_7315092382398562_script_1v30phlnb_getActionArgs,
  });
  const inst_evh_7315092382398562_meterSelected_agw3umkl8 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_script_1v30phlnb,
      event: 'meter:selected',
      displayName: 'script',
    });
  const inst_evh_7315092382398562_meterSelected =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_7315092382398562_meterSelected_agw3umkl8],
      event: 'meter:selected',
    });

  const actions = {
    inst_evh_7315092382398562_save_gf1z7uszn,
    inst_evh_7315092382398562_save,
    inst_evh_7315092382398562_delete_m4rzawhra,
    inst_evh_7315092382398562_delete,
    inst_evh_7315092382398562_meterSelected_agw3umkl8,
    inst_evh_7315092382398562_meterSelected,
  };

  const evh_7315092382398562_save_gf1z7uszn = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_save_gf1z7uszn.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_save = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_save.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_delete_m4rzawhra = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_delete_m4rzawhra.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_delete = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_delete.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_meterSelected_agw3umkl8 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_meterSelected_agw3umkl8.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_meterSelected = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_meterSelected.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpDataTable
    tableId="RentalFormServices"
    :editable="editable"
    :isRowEditable="(p) => !p.locked"
    :value="
      _.orderBy(
        items,
        ['created_at', 'valid_from', 'valid_to'],
        ['desc', 'desc', 'desc']
      )
    "
    :rowTemplate="
      schemaDefaults('CreateRentalService', {
        rental_id: rentalId,
        valid_from: $fn.today(),
      })
    "
    :columns="b_7315092382398562_columns"
    class="h-100"
    ref="tableServices"
    v-if="loaded"
    @save="evh_7315092382398562_save($event, {})"
    @delete="evh_7315092382398562_delete($event, {})"
    @meter:selected="evh_7315092382398562_meterSelected($event, {})"
  >
  </ItpDataTable>
</template>
